import { get } from 'lodash';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { getFilterDropdown, getOnFilter, removeFilter, strong } from 'components/data/helpers';
import { hasTableSiteFilter } from 'components/data/helpers/tableFilterUtils';

import { ConditionalFilterIcon } from './customFilters/ConditionalFilterIcon';
import { DragPriorityControls } from './DragPriorityControls';

import { DataTableContext } from './index';

export const drag = title => ({
  key: 'drag',
  title,
  width: 110,
  render: (_value, record) => <DragPriorityControls itemKey={record?.key ?? record?.id} />,
});

export function useFormattedColumns({ columns = [], isPrioritizing }) {
  const { t } = useTranslation();
  const {
    filteredInfo: currentTableFilters,
    sortedInfo: currentTableSortOrder,
    multiSortDef,
  } = useContext(DataTableContext);

  function getSorterAndFilter({ serverSortAndFilter, onFilter, sorter }) {
    if (serverSortAndFilter) {
      return {
        onFilter: undefined,
        sorter: !!sorter,
      };
    }

    return {
      onFilter,
      sorter: multiSortDef ? undefined : sorter,
    };
  }

  const formattedCols = columns.map(column => {
    const { key: columnKey, filterProperty, sortKey, title } = column;

    const currentColumnFilter = get(currentTableFilters, columnKey, null);
    const currentColumnSortOrder =
      currentTableSortOrder &&
      (currentTableSortOrder.columnKey === columnKey ||
        (sortKey && currentTableSortOrder.columnKey === sortKey))
        ? currentTableSortOrder.order
        : null;

    // FIXME ARS: filterProperty is not an antd thing, it is a shorthand for the column def to flip on some additional defaulted antd props [filterDropdown and onFilter]
    // I think we will remove the shorthand eventually but don't want to change all the files yet
    // AND the way this is used isn't consistent so handling each of the attributes independently
    if (filterProperty) {
      column = {
        ...column,
        filterDropdown: column.filterDropdown
          ? column.filterDropdown
          : !column.filters && getFilterDropdown(),
        onFilter: column.onFilter || getOnFilter({ key: filterProperty }),
      };
    }

    const siteSelected = hasTableSiteFilter(currentTableFilters);
    const filterDisabled = column.requiresSiteFilter && !siteSelected;

    const formattedCol = {
      // explicit global default sorting default
      sortDirections: ['ascend', 'descend'],
      ...column,
      filteredValue: currentColumnFilter,
      sortOrder: currentColumnSortOrder,
      title: strong(title),
      // FIXME ARS: still hate this, but I still need to put some thought in how to reason about this a level up
      ...getSorterAndFilter(column),
      ...(column.requiresSiteFilter
        ? {
            filterIcon: filtered => (
              <ConditionalFilterIcon
                filtered={filtered}
                disabled={filterDisabled}
                disabledTooltip={column.requiresSiteFilterTooltip}
              />
            ),
          }
        : {}),
    };

    return isPrioritizing ? removeFilter(formattedCol) : formattedCol;
  });

  return isPrioritizing ? [drag(t('table.drag', 'Drag / Move')), ...formattedCols] : formattedCols;
}
